// App.js
import React, { Suspense, useEffect } from "react";
import { Routes, Route, Navigate, useNavigate, useLocation } from "react-router-dom";

// Lightweight pages — keep eager (small, needed on first paint)
import LoginPage from "./features/auth/pages/LoginPage";
import RegisterPage from "./features/auth/pages/RegisterPage";
import PasswordResetPage from "./features/auth/pages/PasswordResetPage";
import LandingPage from "./features/marketing/pages/LandingPage";
import NotFoundPage from "./features/marketing/pages/NotFoundPage";

// Shell components — always needed
import PrivateRoute from "./components/common/PrivateRoute";
import ErrorBoundary from "./components/common/ErrorBoundary";
import FeatureErrorBoundary from "./components/common/FeatureErrorBoundary";
import PublicLayout from "./components/layout/PublicLayout";
import AuthenticatedLayout from "./components/layout/AuthenticatedLayout";
import { MerchantShellProvider } from "./components/layout/merchant-shell/MerchantShellContext";
import { useAuth } from "./features/auth/context/AuthContext";
import AppProviders from "./app/AppProviders";
import { UnsavedChangesProvider } from "./app/UnsavedChangesContext";
import "./index.css";

// Heavy pages — lazy loaded
const DashboardPage = React.lazy(() => import("./features/dashboard/pages/DashboardPage"));
const TemplatesPage = React.lazy(() => import("./features/templates/pages/TemplatesPage"));
const TemplateEditorPage = React.lazy(
  () => import("./features/templates/pages/TemplateEditorPage"),
);
const MockupWorkspace = React.lazy(() => import("./features/mockups/pages/MockupWorkspace"));
const DownloadsPage = React.lazy(() => import("./features/jobs/pages/DownloadsPage"));
const DesignLibraryPage = React.lazy(() => import("./features/designs/pages/DesignLibraryPage"));
const UserSettingsPage = React.lazy(() => import("./features/settings/pages/UserSettingsPage"));
const AppsHubPage = React.lazy(() => import("./features/apps/pages/AppsHubPage"));
const AppDetailPage = React.lazy(() => import("./features/apps/pages/AppDetailPage"));
const OnboardingPage = React.lazy(() => import("./features/onboarding/pages/OnboardingPage"));
const DesignStudioPage = React.lazy(
  () => import("./features/design-generator/pages/DesignStudioPage"),
);
const DesignEditorPage = React.lazy(
  () => import("./features/design-generator/pages/DesignEditorPage"),
);
const NewRunPage = React.lazy(() => import("./features/design-generator/pages/NewRunPage"));
// S58 Operator-Backoffice — eigene Shell, deshalb außerhalb der
// AuthenticatedLayout-Gruppe (siehe Route-Gruppe unten).
const AdminLayout = React.lazy(() => import("./components/layout/AdminLayout"));
const OperatorOverviewPage = React.lazy(
  () => import("./features/admin/operator/pages/OperatorOverviewPage"),
);
const OperatorJobsPage = React.lazy(
  () => import("./features/admin/operator/pages/OperatorJobsPage"),
);
const OperatorActivityPage = React.lazy(
  () => import("./features/admin/operator/pages/OperatorActivityPage"),
);
const OperatorUsersPage = React.lazy(
  () => import("./features/admin/operator/pages/OperatorUsersPage"),
);
const DesktopAssistantPage = React.lazy(
  () => import("./features/admin/desktop-assistant/pages/DesktopAssistantPage"),
);
const OperatorIncidentsPage = React.lazy(
  () => import("./features/admin/operator/pages/OperatorIncidentsPage"),
);
const OperatorSystemPage = React.lazy(
  () => import("./features/admin/operator/pages/OperatorSystemPage"),
);
const OperatorUserDetailPage = React.lazy(
  () => import("./features/admin/operator/pages/OperatorUserDetailPage"),
);
const RunDetailPage = React.lazy(() => import("./features/design-generator/pages/RunDetailPage"));
const InsightsPage = React.lazy(() => import("./features/design-generator/pages/InsightsPage"));
// MasterDataPage wird nicht mehr direkt geroutet (Standalone-Zugang
// `/admin/master-data` entfernt — gleicher Content erreichbar via
// /articles → "Produktbasis"-Tab).  Der Lazy-Import passiert jetzt
// transitiv über ArticlesPage.
const ArticlesPage = React.lazy(() => import("./features/admin/articles/pages/ArticlesPage"));
const MarketplacesPage = React.lazy(
  () => import("./features/admin/marketplaces/pages/MarketplacesPage"),
);
const CompanySettingsPage = React.lazy(
  () => import("./features/admin/company/pages/CompanySettingsPage"),
);
// S51: GS1-Verbindung + GTIN-Pool (Vergabe/Nachschub/Ausmusterung).
const Gs1SettingsPage = React.lazy(() => import("./features/admin/gs1/pages/Gs1SettingsPage"));
// S50: Regelwerk des Listing-Generators (SKU-/Titel-Builder, Kategorie-Pfad).
const WizardImageAssignmentsPage = React.lazy(
  () => import("./features/admin/wizard/pages/ImageAssignmentsPage"),
);
const WizardHubPage = React.lazy(() => import("./features/admin/wizard/pages/WizardHubPage"));
const ListingDraftReviewPage = React.lazy(
  () => import("./features/admin/wizard/pages/ListingDraftReviewPage"),
);
const WizardSettingsPage = React.lazy(
  () => import("./features/admin/wizard/pages/WizardSettingsPage"),
);
const OrdersPage = React.lazy(() => import("./features/admin/orders/pages/OrdersPage"));
const StatisticsPage = React.lazy(() => import("./features/admin/statistics/pages/StatisticsPage"));
const AdvertisingPage = React.lazy(
  () => import("./features/admin/advertising/pages/AdvertisingPage"),
);
const AdvertisingCampaignDetailPage = React.lazy(
  () => import("./features/admin/advertising/pages/CampaignDetailPage"),
);
const CustomersPage = React.lazy(() => import("./features/admin/orders/pages/CustomersPage"));
const InvoicesPage = React.lazy(() => import("./features/admin/orders/pages/InvoicesPage"));
const CreditNotesPage = React.lazy(() => import("./features/admin/orders/pages/CreditNotesPage"));
const RipJobsPage = React.lazy(() => import("./features/print-files/pages/RipJobsPage"));
const ReturnsPage = React.lazy(() => import("./features/admin/orders/pages/ReturnsPage"));
const ShippingSettingsPage = React.lazy(
  () => import("./features/admin/shipping/pages/ShippingSettingsPage"),
);
const AutopilotPage = React.lazy(() => import("./features/admin/autopilot/pages/AutopilotPage"));
const SetupHubPage = React.lazy(() => import("./features/admin/setup/pages/SetupHubPage"));
const DocumentTemplatesPage = React.lazy(
  () => import("./features/admin/documents/pages/DocumentTemplatesPage"),
);
const MessagesPage = React.lazy(() => import("./features/admin/messages/pages/MessagesPage"));
const DocumentTemplateEditorPage = React.lazy(
  () => import("./features/admin/documents/pages/DocumentTemplateEditorPage"),
);
const NewBatchWizard = React.lazy(() => import("./features/listings/pages/NewBatchWizard"));
const FeaturesPage = React.lazy(() => import("./features/marketing/pages/FeaturesPage"));
const HelpCenterPage = React.lazy(() => import("./features/marketing/pages/HelpCenterPage"));
const PricingPage = React.lazy(() => import("./features/marketing/pages/PricingPage"));
const PrivacyPage = React.lazy(() => import("./features/marketing/pages/PrivacyPage"));
const TermsPage = React.lazy(() => import("./features/marketing/pages/TermsPage"));
const ImpressumPage = React.lazy(() => import("./features/marketing/pages/ImpressumPage"));

const PageSpinner = () => (
  <div className="min-h-screen bg-background flex items-center justify-center">
    <div className="w-8 h-8 rounded-full border-2 border-primary border-t-transparent animate-spin" />
  </div>
);

/** Umgezogene Route: Restpfad, Query und Anker bleiben erhalten. */
const MovedRoute = ({ from, to }) => {
  const { pathname, search, hash } = useLocation();
  return <Navigate to={{ pathname: to + pathname.slice(from.length), search, hash }} replace />;
};

const AppRoutes = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, isAuthLoading } = useAuth();

  // Redirect authenticated users away from public auth forms. The landing page
  // remains reachable as the explicit "Zur Website" destination from the app shell.
  useEffect(() => {
    if (!isAuthLoading && isAuthenticated && ["/login", "/register"].includes(location.pathname)) {
      navigate("/dashboard");
    }
  }, [isAuthenticated, isAuthLoading, location.pathname, navigate]);

  if (isAuthLoading) {
    return <PageSpinner />;
  }

  return (
    <ErrorBoundary>
      <Suspense fallback={<PageSpinner />}>
        <Routes>
          {/* ── Public routes — PublicHeader + Footer, no Sidebar ── */}
          <Route element={<PublicLayout />}>
            <Route path="/" element={<LandingPage />} />
            <Route path="/products" element={<FeaturesPage />} />
            <Route path="/documentation" element={<HelpCenterPage />} />
            <Route path="/pricing" element={<PricingPage />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/register" element={<RegisterPage />} />
            <Route path="/password-reset" element={<PasswordResetPage />} />
            <Route path="/privacy" element={<PrivacyPage />} />
            <Route path="/terms" element={<TermsPage />} />
            <Route path="/impressum" element={<ImpressumPage />} />
          </Route>

          {/* ── Operator-Backoffice (S58) — eigene Shell, Workspace-Switch ── */}
          <Route
            element={
              <PrivateRoute requireAdmin>
                <AdminLayout />
              </PrivateRoute>
            }
          >
            <Route path="/admin" element={<Navigate to="/admin/overview" replace />} />
            <Route
              path="/admin/incidents"
              element={
                <FeatureErrorBoundary name="operator-admin">
                  <OperatorIncidentsPage />
                </FeatureErrorBoundary>
              }
            />
            <Route
              path="/admin/desktop-assistant"
              element={
                <FeatureErrorBoundary name="operator-admin">
                  <DesktopAssistantPage />
                </FeatureErrorBoundary>
              }
            />
            <Route
              path="/admin/system"
              element={
                <FeatureErrorBoundary name="operator-admin">
                  <OperatorSystemPage />
                </FeatureErrorBoundary>
              }
            />
            <Route
              path="/admin/overview"
              element={
                <FeatureErrorBoundary name="operator-admin">
                  <OperatorOverviewPage />
                </FeatureErrorBoundary>
              }
            />
            <Route
              path="/admin/jobs"
              element={
                <FeatureErrorBoundary name="operator-admin">
                  <OperatorJobsPage />
                </FeatureErrorBoundary>
              }
            />
            <Route
              path="/admin/activity"
              element={
                <FeatureErrorBoundary name="operator-admin">
                  <OperatorActivityPage />
                </FeatureErrorBoundary>
              }
            />
            <Route
              path="/admin/users"
              element={
                <FeatureErrorBoundary name="operator-admin">
                  <OperatorUsersPage />
                </FeatureErrorBoundary>
              }
            />
            <Route
              path="/admin/users/:id"
              element={
                <FeatureErrorBoundary name="operator-admin">
                  <OperatorUserDetailPage />
                </FeatureErrorBoundary>
              }
            />
          </Route>

          {/* ── Authenticated routes — Sidebar + AuthHeader ── */}
          <Route
            element={
              <PrivateRoute>
                <AuthenticatedLayout />
              </PrivateRoute>
            }
          >
            <Route
              path="/dashboard"
              element={
                <FeatureErrorBoundary name="dashboard">
                  <DashboardPage />
                </FeatureErrorBoundary>
              }
            />
            <Route
              path="/templates"
              element={
                <FeatureErrorBoundary name="templates">
                  <TemplatesPage />
                </FeatureErrorBoundary>
              }
            />
            <Route
              path="/templates/:templateId"
              element={
                <FeatureErrorBoundary name="template-editor">
                  <TemplateEditorPage />
                </FeatureErrorBoundary>
              }
            />
            <Route
              path="/mockups"
              element={
                <FeatureErrorBoundary name="mockups">
                  <MockupWorkspace />
                </FeatureErrorBoundary>
              }
            />
            <Route
              path="/designs"
              element={
                <FeatureErrorBoundary name="designs">
                  <DesignLibraryPage />
                </FeatureErrorBoundary>
              }
            />
            <Route path="/mockup-generator" element={<Navigate to="/mockups" replace />} />
            <Route
              path="/downloads"
              element={
                <FeatureErrorBoundary name="downloads">
                  <DownloadsPage />
                </FeatureErrorBoundary>
              }
            />
            <Route
              path="/settings"
              element={
                <FeatureErrorBoundary name="settings">
                  <UserSettingsPage />
                </FeatureErrorBoundary>
              }
            />
            <Route
              path="/apps"
              element={
                <PrivateRoute requireAdmin>
                  <FeatureErrorBoundary name="apps">
                    <AppsHubPage />
                  </FeatureErrorBoundary>
                </PrivateRoute>
              }
            />
            <Route
              path="/apps/:moduleId"
              element={
                <PrivateRoute requireAdmin>
                  <FeatureErrorBoundary name="app-detail">
                    <AppDetailPage />
                  </FeatureErrorBoundary>
                </PrivateRoute>
              }
            />
            <Route
              path="/onboarding"
              element={
                <FeatureErrorBoundary name="onboarding">
                  <OnboardingPage />
                </FeatureErrorBoundary>
              }
            />
            <Route
              path="/design-generator"
              element={
                <FeatureErrorBoundary name="design-generator">
                  <DesignStudioPage />
                </FeatureErrorBoundary>
              }
            />
            <Route
              path="/design-generator/new"
              element={
                <FeatureErrorBoundary name="design-studio-new">
                  <NewRunPage />
                </FeatureErrorBoundary>
              }
            />
            <Route
              path="/design-generator/runs/:id"
              element={
                <FeatureErrorBoundary name="design-studio-run">
                  <RunDetailPage />
                </FeatureErrorBoundary>
              }
            />
            <Route
              path="/design-generator/insights"
              element={
                <FeatureErrorBoundary name="design-studio-insights">
                  <InsightsPage />
                </FeatureErrorBoundary>
              }
            />
            <Route
              path="/design-generator/edit/:jobId/:refId/:resultIndex"
              element={
                <FeatureErrorBoundary name="design-editor">
                  <DesignEditorPage />
                </FeatureErrorBoundary>
              }
            />
            {/* Session 23: Artikelverwaltung mit Tabs (Artikel, Produktbasis,
                Export). Admin-only während der Übergangsphase
                validiert ist — dann requireAdmin raus.

                Der Produktbasis-Tab embedded MasterDataPage direkt — der
                ehemalige Standalone-`/admin/master-data`-Route wurde
                entfernt, weil doppelter Zugang. */}
            <Route
              path="/articles"
              element={
                <PrivateRoute requireAdmin>
                  <FeatureErrorBoundary name="articles">
                    <ArticlesPage />
                  </FeatureErrorBoundary>
                </PrivateRoute>
              }
            />
            <Route
              path="/orders"
              element={
                <PrivateRoute requireAdmin>
                  <FeatureErrorBoundary name="orders">
                    <OrdersPage />
                  </FeatureErrorBoundary>
                </PrivateRoute>
              }
            />
            {/* S62 Verkaufsstatistik — admin-only wie die übrige Wawi-Domäne. */}
            <Route
              path="/statistics"
              element={
                <PrivateRoute requireAdmin>
                  <FeatureErrorBoundary name="statistics">
                    <StatisticsPage />
                  </FeatureErrorBoundary>
                </PrivateRoute>
              }
            />
            {/* S67 Werbung: Marktplatz-Ads (Amazon zuerst) — admin-only.
                /advertising löst auf das zuletzt aktive Profil auf. */}
            {["/advertising", "/advertising/:channel/:profileId"].map((path) => (
              <Route
                key={path}
                path={path}
                element={
                  <PrivateRoute requireAdmin>
                    <FeatureErrorBoundary name="advertising">
                      <AdvertisingPage />
                    </FeatureErrorBoundary>
                  </PrivateRoute>
                }
              />
            ))}
            <Route
              path="/advertising/:channel/:profileId/campaigns/:campaignId"
              element={
                <PrivateRoute requireAdmin>
                  <FeatureErrorBoundary name="advertising">
                    <AdvertisingCampaignDetailPage />
                  </FeatureErrorBoundary>
                </PrivateRoute>
              }
            />
            {/* Kundenstamm (Max 05.07.): eigener Menüpunkt, eigenständige
                Stammdaten-Sicht — Bestellungen bleibt der operative Bereich. */}
            <Route
              path="/customers"
              element={
                <PrivateRoute requireAdmin>
                  <FeatureErrorBoundary name="customers">
                    <CustomersPage />
                  </FeatureErrorBoundary>
                </PrivateRoute>
              }
            />
            {/* Einrichtung (Navigation N2/N3): Hub mit Automatisierung und Kontofunktionen — admin-only. */}
            <Route
              path="/setup"
              element={
                <PrivateRoute requireAdmin>
                  <FeatureErrorBoundary name="setup">
                    <SetupHubPage />
                  </FeatureErrorBoundary>
                </PrivateRoute>
              }
            />
            {/* Versandpartner (Session 42) — admin-only. */}
            <Route
              path="/setup/shipping"
              element={
                <PrivateRoute requireAdmin>
                  <FeatureErrorBoundary name="shipping">
                    <ShippingSettingsPage />
                  </FeatureErrorBoundary>
                </PrivateRoute>
              }
            />
            {/* Autopilot (Session 43): Wenn-Dann-Automatik — admin-only. */}
            <Route
              path="/setup/autopilot"
              element={
                <PrivateRoute requireAdmin>
                  <FeatureErrorBoundary name="autopilot">
                    <AutopilotPage />
                  </FeatureErrorBoundary>
                </PrivateRoute>
              }
            />
            {/* Rechnungs-Übersicht (Buchhaltung, 06.07.) — admin-only. */}
            <Route
              path="/invoices"
              element={
                <PrivateRoute requireAdmin>
                  <FeatureErrorBoundary name="invoices">
                    <InvoicesPage />
                  </FeatureErrorBoundary>
                </PrivateRoute>
              }
            />
            {/* Rip-Aufträge (Druckdateien in der Cloud, 23.09.2026) — admin-only. */}
            <Route
              path="/rip-jobs"
              element={
                <PrivateRoute requireAdmin>
                  <FeatureErrorBoundary name="ripJobs">
                    <RipJobsPage />
                  </FeatureErrorBoundary>
                </PrivateRoute>
              }
            />
            {/* Gutschriften (Buchhaltung, Session 45) — admin-only. */}
            <Route
              path="/invoices/credit-notes"
              element={
                <PrivateRoute requireAdmin>
                  <FeatureErrorBoundary name="creditNotes">
                    <CreditNotesPage />
                  </FeatureErrorBoundary>
                </PrivateRoute>
              }
            />
            <Route
              path="/credit-notes"
              element={<MovedRoute from="/credit-notes" to="/invoices/credit-notes" />}
            />
            {/* Retouren (Session 46, eBay zuerst) — admin-only. */}
            <Route
              path="/returns"
              element={
                <PrivateRoute requireAdmin>
                  <FeatureErrorBoundary name="returns">
                    <ReturnsPage />
                  </FeatureErrorBoundary>
                </PrivateRoute>
              }
            />
            {/* Kundennachrichten (Stufe 1: Amazon-Mail-Relay) — admin-only. */}
            <Route
              path="/messages"
              element={
                <PrivateRoute requireAdmin>
                  <FeatureErrorBoundary name="messages">
                    <MessagesPage />
                  </FeatureErrorBoundary>
                </PrivateRoute>
              }
            />
            {/* Vorlagen (Max 05.07.): Beleg-Vorlagen Lieferschein/Rechnung
                mit visuellem Editor — admin-only wie die Wawi-Domäne. */}
            <Route
              path="/setup/document-templates"
              element={
                <PrivateRoute requireAdmin>
                  <FeatureErrorBoundary name="document-templates">
                    <DocumentTemplatesPage />
                  </FeatureErrorBoundary>
                </PrivateRoute>
              }
            />
            <Route
              path="/setup/document-templates/:id"
              element={
                <PrivateRoute requireAdmin>
                  <FeatureErrorBoundary name="document-template-editor">
                    <DocumentTemplateEditorPage />
                  </FeatureErrorBoundary>
                </PrivateRoute>
              }
            />
            <Route
              path="/setup/marketplaces"
              element={
                <PrivateRoute requireAdmin>
                  <FeatureErrorBoundary name="marketplaces">
                    <MarketplacesPage />
                  </FeatureErrorBoundary>
                </PrivateRoute>
              }
            />
            <Route
              path="/setup/company"
              element={
                <PrivateRoute requireAdmin>
                  <FeatureErrorBoundary name="company-settings">
                    <CompanySettingsPage />
                  </FeatureErrorBoundary>
                </PrivateRoute>
              }
            />
            <Route
              path="/setup/gs1"
              element={
                <PrivateRoute requireAdmin>
                  <FeatureErrorBoundary name="gs1-settings">
                    <Gs1SettingsPage />
                  </FeatureErrorBoundary>
                </PrivateRoute>
              }
            />
            {/* Alte Einrichtungs-Routen leiten in den Hub um. */}
            <Route
              path="/marketplaces/*"
              element={<MovedRoute from="/marketplaces" to="/setup/marketplaces" />}
            />
            <Route
              path="/shipping/*"
              element={<MovedRoute from="/shipping" to="/setup/shipping" />}
            />
            <Route
              path="/document-templates/*"
              element={<MovedRoute from="/document-templates" to="/setup/document-templates" />}
            />
            <Route path="/company/*" element={<MovedRoute from="/company" to="/setup/company" />} />
            <Route path="/gs1/*" element={<MovedRoute from="/gs1" to="/setup/gs1" />} />
            <Route
              path="/autopilot/*"
              element={<MovedRoute from="/autopilot" to="/setup/autopilot" />}
            />
            <Route
              path="/wizard"
              element={
                <PrivateRoute requireAdmin>
                  <FeatureErrorBoundary name="wizard-hub">
                    <WizardHubPage />
                  </FeatureErrorBoundary>
                </PrivateRoute>
              }
            />
            <Route
              path="/wizard/batches/:id"
              element={
                <PrivateRoute requireAdmin>
                  <FeatureErrorBoundary name="wizard-batch-review">
                    <ListingDraftReviewPage />
                  </FeatureErrorBoundary>
                </PrivateRoute>
              }
            />
            <Route
              path="/wizard-settings"
              element={
                <PrivateRoute requireAdmin>
                  <FeatureErrorBoundary name="wizard-settings">
                    <WizardSettingsPage />
                  </FeatureErrorBoundary>
                </PrivateRoute>
              }
            />
            <Route
              path="/wizard-settings/images"
              element={
                <PrivateRoute requireAdmin>
                  <FeatureErrorBoundary name="wizard-settings-images">
                    <WizardImageAssignmentsPage />
                  </FeatureErrorBoundary>
                </PrivateRoute>
              }
            />
            <Route
              path="/articles/design/:designKey"
              element={
                <PrivateRoute requireAdmin>
                  <FeatureErrorBoundary name="articles-design-detail">
                    <ArticlesPage />
                  </FeatureErrorBoundary>
                </PrivateRoute>
              }
            />
            <Route
              path="/articles/:id"
              element={
                <PrivateRoute requireAdmin>
                  <FeatureErrorBoundary name="articles-detail">
                    <ArticlesPage />
                  </FeatureErrorBoundary>
                </PrivateRoute>
              }
            />
            {/* Wizard (Batch) unter /articles/new; /listings* redirecten in
                die Artikel-Fusion (Menüpunkt „Listings" entfernt). */}
            <Route
              path="/articles/new"
              element={
                <PrivateRoute requireAdmin>
                  <FeatureErrorBoundary name="listings-wizard">
                    <NewBatchWizard />
                  </FeatureErrorBoundary>
                </PrivateRoute>
              }
            />
            <Route path="/listings" element={<Navigate to="/articles" replace />} />
            <Route path="/listings/new" element={<Navigate to="/articles/new" replace />} />
            {/* AI-Metadata-Batch-Tool (Session 24.5) entfernt 02.07.2026 —
                der Listing-Wizard erstellt Artikel direkt; /ai-metadata war
                der abgelöste Vorgänger-Pfad. */}
            <Route path="/ai-metadata" element={<Navigate to="/articles" replace />} />
          </Route>

          {/* ── 404 fallback — must be last ── */}
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </Suspense>
    </ErrorBoundary>
  );
};

function App() {
  return (
    <AppProviders>
      <UnsavedChangesProvider>
        <MerchantShellProvider>
          <AppRoutes />
        </MerchantShellProvider>
      </UnsavedChangesProvider>
    </AppProviders>
  );
}

export default App;
